import { useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { routes } from '../../lib/assessment/routes';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Password recovery — request and update (V2-018).
 *
 * Two surfaces, one flow:
 *
 * 1. `PasswordResetRequest` at `routes.resetPassword` sends the recovery email.
 * 2. `PasswordUpdate` at `routes.updatePassword` is the target of the emailed
 *    link and sets the new password.
 *
 * Neither talks to the API. Supabase Auth performs both operations directly from
 * the browser through the auth-only client boundary (ADR-0006 keeps app data on
 * the Worker `/api/*` path; authentication is the documented exception), so this
 * adds no Worker route and no new server surface.
 *
 * The update surface is gated on the store's `password-recovery` status rather
 * than on the presence of a session. A recovery session is a real Supabase
 * session, so "is there a session?" would admit any signed-in visitor; only the
 * store knows the session arrived through PASSWORD_RECOVERY.
 *
 * The focus-ring classes are repeated literally on every `Link` below and must
 * stay that way. `visualSystem.test.ts` parses each link's `className` *source
 * text* and requires `focus-visible:ring-1` and `focus-visible:ring-ring` to be
 * readable there, so hoisting them into a shared constant fails the suite even
 * though the rendered markup is identical.
 */

/**
 * Where Supabase sends the recovery link. Derived from the live origin rather
 * than configured, so preview and production each point at themselves. The value
 * must be present in the Supabase Auth redirect allowlist for the link to work,
 * which is hosted configuration and outside this slice.
 */
function recoveryRedirectUrl(): string {
  return `${window.location.origin}${routes.updatePassword}`;
}

type RequestOutcome =
  | { kind: 'idle' }
  | { kind: 'sent' }
  | { kind: 'failed'; message: string };

export function PasswordResetRequest() {
  const { resetPasswordForEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<RequestOutcome>({ kind: 'idle' });

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);

    try {
      const { error: authError } = await resetPasswordForEmail(email, {
        redirectTo: recoveryRedirectUrl(),
      });

      // Supabase answers success for an unregistered address by design, so a
      // returned error is never "no such account": it is a rejected redirect
      // URL, a rate limit, a misconfiguration, or a service failure. Showing it
      // reports a real problem without disclosing whether the address exists.
      // Reporting success for a request that failed would be worse than either.
      if (authError) {
        setOutcome({ kind: 'failed', message: authError.message });
      } else {
        setOutcome({ kind: 'sent' });
      }
    } catch {
      // A thrown rejection means the request never completed at all — a network
      // failure, not an answer about this address.
      setOutcome({
        kind: 'failed',
        message:
          'The request could not be sent. Check your connection and try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  if (outcome.kind === 'sent') {
    return (
      <section
        className="mx-auto w-full max-w-md space-y-6"
        data-testid="password-reset-sent"
      >
        <h1 className="text-3xl font-bold">Check your email</h1>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-sm leading-relaxed text-muted-foreground">
            If an account exists for {email}, a link to set a new password is on
            its way. The link can be used once and expires after a short time.
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          <Link
            href={routes.home}
            className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Back to start
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-md space-y-6">
      <div className="space-y-3">
        <h1 className="text-3xl font-bold">Reset your password</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Enter the email address on your account and we will send a link to set
          a new password.
        </p>
      </div>

      <form
        className="flex flex-col gap-3"
        data-testid="password-reset-request-form"
        onSubmit={(event) => {
          void onSubmit(event);
        }}
      >
        <label className="text-sm font-semibold" htmlFor="reset-email">
          Email
        </label>
        <Input
          id="reset-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Working…' : 'Send reset link'}
        </Button>
        {outcome.kind === 'failed' ? (
          <p className="text-destructive" data-testid="password-reset-error">
            {outcome.message}
          </p>
        ) : null}
      </form>

      <p className="text-sm text-muted-foreground">
        <Link
          href={routes.home}
          className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Back to start
        </Link>
      </p>
    </section>
  );
}

export function PasswordUpdate() {
  const { status, updateUser } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (password !== confirmation) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { error: authError } = await updateUser({ password });
      if (authError) {
        // Supabase owns password policy; its message is shown rather than a
        // local rule, so the two can never disagree.
        setError(authError.message);
      } else {
        setDone(true);
      }
    } catch {
      setError(
        'The password could not be updated. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  // Checked before the status gate. A successful update clears the recovery
  // latch, so `status` is no longer `password-recovery` by this point; reading
  // the gate first would replace the confirmation with a refusal.
  if (done) {
    return (
      <section
        className="mx-auto w-full max-w-md space-y-6"
        data-testid="password-update-done"
      >
        <h1 className="text-3xl font-bold">Password updated</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          You are signed in with your new password.
        </p>
        <p className="text-sm text-muted-foreground">
          {/* The reset ends signed in, so the dashboard is where "continue"
              leads. It previously led to the marketing home, which was the only
              destination that existed when this was written. */}
          <Link
            href={routes.dashboard}
            data-testid="password-update-continue"
            className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Continue to your dashboard
          </Link>
        </p>
      </section>
    );
  }

  if (status === 'loading') {
    return <p className="text-muted-foreground">Checking session…</p>;
  }

  // Fail closed. Anything other than a live recovery session is refused, which
  // covers an expired link, a link already used, a direct visit, and an ordinary
  // signed-in visitor who typed the URL.
  if (status !== 'password-recovery') {
    return (
      <section
        className="mx-auto w-full max-w-md space-y-6"
        data-testid="password-update-refused"
      >
        <h1 className="text-3xl font-bold">This link is not active</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Password reset links can be used once and expire after a short time.
          Request a new one to continue.
        </p>
        <p className="text-sm text-muted-foreground">
          <Link
            href={routes.resetPassword}
            className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Request a new reset link
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-md space-y-6">
      <div className="space-y-3">
        <h1 className="text-3xl font-bold">Set a new password</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Choose a new password for your account.
        </p>
      </div>

      <form
        className="flex flex-col gap-3"
        data-testid="password-update-form"
        onSubmit={(event) => {
          void onSubmit(event);
        }}
      >
        <label className="text-sm font-semibold" htmlFor="new-password">
          New password
        </label>
        <Input
          id="new-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
        />

        <label className="text-sm font-semibold" htmlFor="confirm-password">
          Confirm new password
        </label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          autoComplete="new-password"
          required
        />

        <Button type="submit" disabled={busy}>
          {busy ? 'Working…' : 'Set new password'}
        </Button>

        {error ? (
          <p className="text-destructive" data-testid="password-update-error">
            {error}
          </p>
        ) : null}
      </form>
    </section>
  );
}
